import { useCallback, useEffect, useRef } from "react";

/** How long a row must stay mostly visible before it counts as read. */
export const AUTO_READ_DWELL_MS = 1000;

/** Share of a row that must be on screen for it to count as visible. */
export const AUTO_READ_VISIBLE_RATIO = 0.6;

/** Rows that finish their dwell within this window are marked read together. */
export const AUTO_READ_FLUSH_MS = 300;

export interface UseAutoMarkReadOptions {
  /** Receives each batch of ids that were seen; marks them read. */
  onRead: (ids: string[]) => void;
  dwellMs?: number;
  visibleRatio?: number;
}

type RowRef = (element: HTMLElement | null) => void;

/**
 * Marks list rows read once they have been at least `visibleRatio` on screen
 * for `dwellMs`, so flicking through a list doesn't read everything. Seen ids
 * are batched into one `onRead` call; a batch still pending on unmount is
 * flushed so closing the list right after reading doesn't lose it.
 *
 * Returns `rowRef(id)`: attach the result as a row's ref to watch it, or pass
 * `null` for rows that shouldn't be auto-read. The observer uses the viewport
 * as its root, which still respects clipping by scrolling ancestors (the
 * Alerts popover / sheet).
 */
export function useAutoMarkRead({
  onRead,
  dwellMs = AUTO_READ_DWELL_MS,
  visibleRatio = AUTO_READ_VISIBLE_RATIO,
}: UseAutoMarkReadOptions): (id: string | null) => RowRef {
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;

  const observerRef = useRef<IntersectionObserver | null>(null);
  const idByElement = useRef(new Map<Element, string>());
  const elementById = useRef(new Map<string, HTMLElement>());
  const dwellTimers = useRef(new Map<string, number>());
  const queued = useRef(new Set<string>());
  const pending = useRef<string[]>([]);
  const flushTimer = useRef<number | null>(null);
  const refCache = useRef(new Map<string, RowRef>());

  const flush = useCallback(() => {
    if (flushTimer.current !== null) {
      window.clearTimeout(flushTimer.current);
      flushTimer.current = null;
    }
    if (pending.current.length === 0) return;
    const ids = pending.current;
    pending.current = [];
    onReadRef.current(ids);
  }, []);

  const clearDwell = useCallback((id: string) => {
    const timer = dwellTimers.current.get(id);
    if (timer === undefined) return;
    window.clearTimeout(timer);
    dwellTimers.current.delete(id);
  }, []);

  const markSeen = useCallback(
    (id: string) => {
      dwellTimers.current.delete(id);
      if (queued.current.has(id)) return;
      queued.current.add(id);
      pending.current.push(id);
      if (flushTimer.current === null) {
        flushTimer.current = window.setTimeout(flush, AUTO_READ_FLUSH_MS);
      }
    },
    [flush],
  );

  const handleEntries = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const id = idByElement.current.get(entry.target);
        if (!id || queued.current.has(id)) return;
        const visible =
          entry.isIntersecting && entry.intersectionRatio >= visibleRatio;
        if (!visible) {
          clearDwell(id);
          return;
        }
        if (dwellTimers.current.has(id)) return;
        dwellTimers.current.set(
          id,
          window.setTimeout(() => markSeen(id), dwellMs),
        );
      });
    },
    [clearDwell, dwellMs, markSeen, visibleRatio],
  );

  const getObserver = useCallback(() => {
    if (
      !observerRef.current &&
      typeof window.IntersectionObserver === "function"
    ) {
      observerRef.current = new window.IntersectionObserver(handleEntries, {
        threshold: [0, visibleRatio],
      });
    }
    return observerRef.current;
  }, [handleEntries, visibleRatio]);

  const unwatch = useCallback(
    (id: string) => {
      const element = elementById.current.get(id);
      if (!element) return;
      observerRef.current?.unobserve(element);
      idByElement.current.delete(element);
      elementById.current.delete(id);
      clearDwell(id);
    },
    [clearDwell],
  );

  const watch = useCallback(
    (id: string, element: HTMLElement) => {
      if (elementById.current.get(id) === element) return;
      unwatch(id);
      const observer = getObserver();
      if (!observer) return;
      idByElement.current.set(element, id);
      elementById.current.set(id, element);
      observer.observe(element);
    },
    [getObserver, unwatch],
  );

  useEffect(() => {
    const timers = dwellTimers.current;
    return () => {
      flush();
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [flush]);

  return useCallback(
    (id: string | null): RowRef => {
      if (id === null) return NOOP_REF;
      let ref = refCache.current.get(id);
      if (!ref) {
        ref = (element) => (element ? watch(id, element) : unwatch(id));
        refCache.current.set(id, ref);
      }
      return ref;
    },
    [unwatch, watch],
  );
}

const NOOP_REF: RowRef = () => {};
